/**
 * 站内信系统 - API 封装
 * 
 * 提供消息管理相关的 API 接口
 * 
 * @author AI Assistant
 * @created 2025-12-02
 */

import httpRequest from '@/utils/api'
import type {
  MessageDetail,
  MessageDetailAPIResponse,
  CreateMessageRequest,
  UpdateMessageRequest,
  MessageListParams,
  MessageListResponse,
} from '@/types/message'

enum API {
  /** 发送消息 */
  sendMessage = '/admin/messages',
  /** 获取消息列表 */
  getMessages = '/admin/messages',
  /** 获取消息详情 */
  getMessage = '/admin/messages/:messageId',
  /** 更新消息 */
  updateMessage = '/admin/messages/:messageId',
  /** 删除消息 */
  deleteMessage = '/admin/messages/:messageId',
}

/**
 * 发送系统公告
 * 
 * @param data 创建消息请求数据
 * @returns 消息详情响应
 */
export const sendMessageAPI = (data: CreateMessageRequest) => {
  return httpRequest.post<MessageDetail>({
    url: API.sendMessage,
    data,
  })
}

/**
 * 获取消息列表
 *
 * @param params 查询参数
 * @returns 消息列表响应
 */
export const getMessagesAPI = (params?: MessageListParams) => {
  // 转换参数为 snake_case
  const queryParams: Record<string, any> = {
    page: params?.page,
    limit: params?.limit,
  }

  if (params?.priority) {
    queryParams.priority = params.priority
  }
  if (params?.search) {
    queryParams.search = params.search
  }

  return httpRequest.get<MessageListResponse>({
    url: API.getMessages,
    params: queryParams,
  })
}

/**
 * 获取消息详情
 * 
 * @param messageId 消息 ID
 * @returns 消息详情响应
 */
export const getMessageAPI = (messageId: number) => {
  return httpRequest.get<MessageDetailAPIResponse>({
    url: API.getMessage.replace(':messageId', messageId.toString()),
  })
}

/**
 * 更新消息
 * 
 * @param messageId 消息 ID
 * @param data 更新请求数据
 * @returns 消息详情响应
 */
export const updateMessageAPI = (messageId: number, data: UpdateMessageRequest) => {
  return httpRequest.put<MessageDetail>({
    url: API.updateMessage.replace(':messageId', messageId.toString()),
    data,
  })
}

/**
 * 删除消息
 * 
 * @param messageId 消息 ID
 * @returns 删除响应
 */
export const deleteMessageAPI = (messageId: number) => {
  return httpRequest.delete({
    url: API.deleteMessage.replace(':messageId', messageId.toString()),
  })
}

